import {
  Box,
  Paper,
  Theme,
  ThemeOptions
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { GridColDef, GridColumnVisibilityModel, } from '@mui/x-data-grid';
import { jaJP } from '@mui/x-data-grid/locales';
import {
  DataGridPremium, GridCellEditStopParams, GridCellParams, GridColumnResizeParams,
  GridFilterItem,
  GridFilterModel,
  GridRowSelectionModel,
  GridSortModel,
  useGridApiRef } from '@mui/x-data-grid-premium';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { format } from 'date-fns-tz';
import { useSnackbar } from 'notistack';
import React, { FC, memo, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { CustomLoadingOverlay } from 'src/components/utils/CustomLoadingOverlay';
import SalesOrderForm from 'src/components/V2SalesOrderForm';
import { appBarHeight } from 'src/constants/layout';
import LicenseContext from 'src/contexts/LicenseContext';
import datetimeDecorator from 'src/decorators/datetime.decorator';
import { RansackPredicateEntity } from 'src/entities/RansackPredicate.entity';
import { SalesOrderResponseEntity } from 'src/entities/SalesOrder.response.entity';
import { useDeleteAllSalesOrdersMutation } from 'src/hooks/useDeleteAllSalesOrders.mutation';
import { usePartnerCompaniesRequest } from 'src/hooks/usePartnerCompanies.request';
import { useQueryPlanningsDrivers } from 'src/hooks/useQueryPlanningsDrivers';
import { useMutationSalesOrder } from 'src/hooks/useQuerySalesOrders';
import { useSalesOrdersRequest as usePaginationRequest, queryKey } from 'src/hooks/useSalesOrders.request';
import { dataGridUtil } from 'src/utils/dataGrid.util';
import operatorValueUtil from 'src/utils/operatorValue.util';
import stringUtil from 'src/utils/string.util';

import { SelectAllSnackbars } from '../common/SelectAllSnackbars';
import CoordinationEditDialog from '../V2PlacesPresenter/PositionEditDialog';

import { Columns } from './Columns';
import CustomToolbar from './CustomToolbar';

interface CustomToolbarProps {
  toggleDialogIsOpen: () => void;
  onClickDeleteTrucks: () => void;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  canDeleteAll: boolean;
  deleteAll: (settledFnk: () => void) => void;
  handleDownloadCSV: () => void;
}

const V2SalesOrdersPresenter: FC = memo(() => {
  const localStorageItemPrefix = 'sales-order-columns';
  const theme: Theme & ThemeOptions = useTheme();
  const licenseContext = useContext(LicenseContext);
  const { enqueueSnackbar } = useSnackbar();
  const defaultRowHeight = 52;
  const queryClient = useQueryClient();

  const DEAFULT_PAR_PAGE = 50;

  const [dialogIsOpen, setDialogIsOpen] = useState<boolean>(false);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({});
  const [isLoading, setIsLoading]: [boolean, ((value: (((prevState: boolean) => boolean) | boolean)) => void)] = useState<boolean>(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [rowHeight, setRowHeight] = useState<number>(defaultRowHeight);
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(DEAFULT_PAR_PAGE);
  const [rowCount, setRowCount] = useState<number>(0);
  const [sortCondition, setSortCondition] = useState<string>('');
  const [orConditions, setOrConditions] = useState<RansackPredicateEntity[]>([]);
  const [andConditions, setAndConditions] = useState<RansackPredicateEntity[]>([]);
  const [searchCondition, setSearchCondition] = useState<string>('');
  const [canDeleteAll, setCanDeleteAll] = useState<boolean>(false);
  const [editTargetSalesOrder, setEditTargetSalesOrder] = useState<SalesOrderResponseEntity>();
  const [salesOrderEditEntity, setSalesOrderEditEntity] = useState<SalesOrderResponseEntity>(null);
  const [loadingCoordinationEditDialogIsOpen, setLoadingCoordinationEditDialogIsOpen] = useState(false);
  const [unloadingCoordinationEditDialogIsOpen, setUnloadingCoordinationEditDialogIsOpen] = useState(false);

  const apiRef = useGridApiRef();
  const today = datetimeDecorator.toYyyyMmDd(new Date());
  const { data: driverEntities } = useQueryPlanningsDrivers(today, today);

  const { data: requestData, isLoading: requestIsLoading } = usePaginationRequest(page, sortCondition, searchCondition, true, pageSize);
  const { data: allRequestData } = usePaginationRequest(0, sortCondition, searchCondition, !requestIsLoading, 100000);
  const { update: updateSalesOrder, delete: deleteSalesOrders } = useMutationSalesOrder();
  const { post } = useDeleteAllSalesOrdersMutation();
  const { data: partners } = usePartnerCompaniesRequest(0, '', '', true, 10000);

  const deleteAll = (settledFnk: () => void) => {
    if (window.confirm(`選択された ${rowCount} 件の注文を削除します。よろしいですか？`)) {
      setIsLoading(true);
      setSelectedIds([]);

      post.mutate(null, {
        onSuccess: () => {
          enqueueSnackbar('すべて削除しました');
        },
        onError: (error: AxiosError<{ message: string; }>) => {
          enqueueSnackbar(error.response?.data?.message);
        },
        onSettled: () => {
          void queryClient.resetQueries({
            queryKey: [queryKey]
          })
            .finally(() => {
              settledFnk();
              setIsLoading(false);
            });
        }
      });
    }
  };

  const toggleDialogIsOpen = () => {
    setEditTargetSalesOrder(null);
    setDialogIsOpen(!dialogIsOpen);
  };

  const dialogOnClose = () => {
    setEditTargetSalesOrder(null);
    setDialogIsOpen(false);
  };

  const closeLoadingCoordinationEditDialog = () => {
    setSalesOrderEditEntity(null);
    setLoadingCoordinationEditDialogIsOpen(!loadingCoordinationEditDialogIsOpen);
  };

  const closeUnloadingCoordinationEditDialog = () => {
    setSalesOrderEditEntity(null);
    setUnloadingCoordinationEditDialogIsOpen(!unloadingCoordinationEditDialogIsOpen);
  };

  const openLoadingMapDialog = (entity: SalesOrderResponseEntity) => {
    setSalesOrderEditEntity(entity);
    setLoadingCoordinationEditDialogIsOpen(true);
  };

  const openUnloadingMapDialog = (entity: SalesOrderResponseEntity) => {
    setSalesOrderEditEntity(entity);
    setUnloadingCoordinationEditDialogIsOpen(true);
  };

  const updateLoadingLatLng = (lat: string, lng: string) => {
    salesOrderEditEntity.loadingLatitude = lat;
    salesOrderEditEntity.loadingLongitude = lng;
    salesOrderEditEntity.updatePosition = true;

    updateEntity(salesOrderEditEntity, closeLoadingCoordinationEditDialog);
  };

  const loadingCoordinationEditDialog = (
    salesOrderEditEntity && loadingCoordinationEditDialogIsOpen && (
      <CoordinationEditDialog
        key="loadingCoordinationEditDialog"
        dialogIsOpen={loadingCoordinationEditDialogIsOpen}
        closeDialog={closeLoadingCoordinationEditDialog}
        loading={isLoading}
        lat={salesOrderEditEntity.loadingLatitude}
        lng={salesOrderEditEntity.loadingLongitude}
        updateLatLng={updateLoadingLatLng}
        isOrder
      />
    )
  );

  const updateUnloadingLatLng = (lat: string, lng: string) => {
    salesOrderEditEntity.unloadingLatitude = lat;
    salesOrderEditEntity.unloadingLongitude = lng;
    salesOrderEditEntity.updatePosition = true;

    updateEntity(salesOrderEditEntity, closeLoadingCoordinationEditDialog);
  };

  const updateEntity = (entity: SalesOrderResponseEntity, onSuccess?: () => void,) => {
    setIsLoading(true);
    updateSalesOrder.mutate(entity, {
      onSuccess: () => { onSuccess?.(); },
      onError: (e: AxiosError<{ data: string[]; }>) => { enqueueSnackbar(e.response.data.data.join(', ')); },
      onSettled: () => {
        setIsLoading(false);
      }
    });
  };

  const unloadingCoordinationEditDialog = (
    salesOrderEditEntity && unloadingCoordinationEditDialogIsOpen && (
      <CoordinationEditDialog
        key="unloadingCoordinationEditDialog"
        dialogIsOpen={unloadingCoordinationEditDialogIsOpen}
        closeDialog={closeUnloadingCoordinationEditDialog}
        loading={isLoading}
        lat={salesOrderEditEntity.unloadingLatitude}
        lng={salesOrderEditEntity.unloadingLongitude}
        updateLatLng={updateUnloadingLatLng}
        isOrder
      />
    )
  );

  const openEdit = (entity: SalesOrderResponseEntity) => {
    setEditTargetSalesOrder(entity);
    setDialogIsOpen(true);
  };

  const onCellDoubleClick = (params: GridCellParams, event: React.MouseEvent) => {
    if (params.rowNode.type === 'group') return;

    const order = params.row as SalesOrderResponseEntity;
    if (!params.isEditable) {
      event.stopPropagation();
      openEdit(order);
    }
  };

  const onCellEditStop = (params: GridCellEditStopParams) => {
    const editableFields = ['truck_number'];
    if (!editableFields.includes(params.field)) return;

    const entity = requestData.data.find((it) => it.id === params.id);
    updateEntity({
      ...entity,
      [params.field]: params.value as string,
    });
  };

  const dataGridColumns: GridColDef[] = useMemo(() => Columns(
    openLoadingMapDialog,
    openUnloadingMapDialog,
    localStorageItemPrefix,
    partners?.data || [],
    updateEntity,
    openEdit,
    driverEntities,
    apiRef,
  ).filter((col) => {
    if (!licenseContext.config) return true;
    if (licenseContext.config.selectable_companies?.length > 1) return true;

    return col.field !== 'company_name';
  }), [licenseContext.config, localStorageItemPrefix, partners?.data, updateEntity, openEdit]);

  const columnsWithCustomFields = useMemo(() => {
    const customInputFields = licenseContext?.config?.custom_input_fields || [];

    const customFields = customInputFields.map((field, index) => (
      {
        field: `custom_input_fields_${index}`,
        headerName: field,
        width: 200,
        valueGetter: (_, row: SalesOrderResponseEntity) => row
          .customInputFields
          ?.find((it) => it.key === field)?.value
      }
    ));
    const orderedFields = dataGridUtil.getColumnOrders(localStorageItemPrefix);

    return [
      ...dataGridColumns,
      ...customFields
    ].sort((a, b) => orderedFields.indexOf(a.field) - orderedFields.indexOf(b.field));
  }, [dataGridColumns, licenseContext?.config?.custom_input_fields, localStorageItemPrefix]);

  // const getCurrentPageRows = () => rowData.slice(page * pageSize, (page + 1) * pageSize);

  const dataGridOnSelectionModelChange = (rowSelectionModel: GridRowSelectionModel) => {
    const ids = Array.from(rowSelectionModel.ids).map((id) => Number(id));
    if (rowSelectionModel.type === 'include') {
      setSelectedIds(ids);
    } else if (rowSelectionModel.type === 'exclude') {
      const allIds = requestData?.data.map((it) => it.id) || [];
      setSelectedIds(allIds.filter((id) => !ids.includes(id)));
    }
  };

  const removeSelectedOrders = async () => {
    if (!selectedIds.length) return;

    if (!window.confirm(`${selectedIds.length}件の案件を削除します。よろしいですか？`)) return;

    setSelectedIds([]);
    setIsLoading(true);

    try {
      await deleteSalesOrders.mutateAsync(selectedIds);
      enqueueSnackbar(`${selectedIds.length}件の注文を削除しました`);
      setSelectedIds([]);
    } catch (error) {
      const axiosError = error as unknown as AxiosError<{ message: string }>;
      enqueueSnackbar(axiosError.response.data.message);
      throw error;
    } finally {
      void queryClient.resetQueries({
        queryKey: [queryKey]
      })
        .finally(() => {
          setIsLoading(false);
          setSelectedIds([]);
        });
    }
  };

  const handleSortModelChange = (sortModel: GridSortModel) => {
    if (sortModel.length === 0) {
      setSortCondition('');
      return;
    }
    setSortCondition(
      [
        stringUtil.toSnakeCase(sortModel[0].field),
        sortModel[0].sort
      ].join('+')
    );
  };

  const onFilterChange = ((filterModel: GridFilterModel) => {
    const items = filterModel?.items;

    if (!items || items.length === 0) {
      setOrConditions([]);
      setAndConditions([]);
      return;
    }

    const ransackPredicates: RansackPredicateEntity[] = items.flatMap((item: GridFilterItem) => operatorValueUtil.convertToRansackPredicate(
        stringUtil.toSnakeCase(item.field || ''),
        item.operator || '',
        item.value as string | string[] | undefined,
        filterModel.logicOperator || 'and'
      ));

    setOrConditions(ransackPredicates.filter((predicate) => predicate?.link === 'or'));
    setAndConditions(ransackPredicates.filter((predicate) => predicate?.link === 'and'));
  });

  useEffect(() => {
    setSearchCondition(
      [
        operatorValueUtil.convertToQueryParams('or_conditions', orConditions),
        operatorValueUtil.convertToQueryParams('and_conditions', andConditions),
      ].filter((maybe) => maybe).join('&')
    );
  }, [andConditions, orConditions]);

  useEffect(() => {
    if (!requestData) return;
    setRowCount(requestData.totalCount);
  }, [requestData]);

  useEffect(() => {
    const canPaginate = rowCount > pageSize;

    if (!canPaginate) {
      setCanDeleteAll(false);
    }

    const selectedAll = selectedIds?.length === pageSize;

    setCanDeleteAll(selectedAll);
  }, [selectedIds, pageSize, rowCount]);

  const handleDownloadCsv = () => {
    const options = {
      utf8WithBom: true,
      fileName: `LOG-受注一覧-${format(new Date(), 'yyyyMMddHHmmss', { timeZone: 'Asia/Tokyo' })}`,
    };
    apiRef.current.getAllColumns().forEach((column) => {
      if (column.type === 'date') {
        column.type = 'string';
      }
    });

    apiRef.current.setRows(allRequestData.data);
    apiRef.current.exportDataAsCsv(options);
    apiRef.current.setRows(requestData.data);
  };

  // 不要な関数を削除

  const dataGridHgieht = useMemo(() => {
    const offset = appBarHeight;
    const spacing = 4;

    return `calc(100vh - ${theme.spacing(spacing)} - ${offset}px)`;
  }, [theme]);

  const onColumnOrderChange = useCallback(() => {
    const columnOrders = apiRef.current.getAllColumns().map((col) => col.field);
    dataGridUtil.setColumnOrders(localStorageItemPrefix, columnOrders);
  }, [apiRef, localStorageItemPrefix]);

  if (!requestData) return null;

  return (
    <>
      <Helmet>
        <title>受注一覧</title>
      </Helmet>
      <Box p={1}>
        {loadingCoordinationEditDialog}
        {unloadingCoordinationEditDialog}
        {dialogIsOpen && (
          <SalesOrderForm
            dialogIsOpen={dialogIsOpen}
            dialogOnClose={dialogOnClose}
            onClose={dialogOnClose}
            entity={editTargetSalesOrder}
          />
        )}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center'
          }}
        >
          <SelectAllSnackbars
            totalPageCount={requestData.totalPageCount || 1}
            totalCount={rowCount || 0}
            perPage={pageSize}
            allIds={allRequestData?.data.map((it) => it.id) || requestData?.allIds || []}
            selectedIds={selectedIds}
            apiRef={apiRef}
            setSelectedIds={setSelectedIds}
          />
          <Box flexGrow={1} sx={{ width: '100%', }}>
            <Paper
              style={{
                width: '100%',
                height: dataGridHgieht,
              }}
            >
              <DataGridPremium
                apiRef={apiRef}
                rows={requestData?.data || []}
                columns={columnsWithCustomFields}
                pagination
                checkboxSelection
                disableRowGrouping
                slots={{
                  loadingOverlay: CustomLoadingOverlay,
                  toolbar: CustomToolbar
                }}
                slotProps={{
                  toolbar: {
                    removeSelectedOrders,
                    toggleDialogIsOpen,
                    setIsLoading,
                    isLoading,
                    canDeleteAll,
                    deleteAll,
                    handleDownloadCsv,
                  } as Partial<CustomToolbarProps>
                }}
                rowHeight={rowHeight}
                localeText={jaJP.components.MuiDataGrid.defaultProps.localeText}
                loading={requestIsLoading || isLoading}
                onRowSelectionModelChange={dataGridOnSelectionModelChange}
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={(newModel: GridColumnVisibilityModel) => {
                  dataGridUtil.setSavedColumnVisibilities(localStorageItemPrefix, newModel);
                }}
                // rowSelectionModel={selectedIds}
                sortingMode="server"
                paginationMode="server"
                filterMode="server"
                paginationModel={{
                  page,
                  pageSize,
                }}
                onPaginationModelChange={({ page: newPage, pageSize: newPageSize }) => {
                  setPage(newPage);
                  setPageSize(Number(newPageSize));
                  setSelectedIds([]);
                }}
                rowCount={rowCount}
                onSortModelChange={handleSortModelChange}
                onFilterModelChange={onFilterChange}
                onColumnWidthChange={(params: GridColumnResizeParams) => {
                  dataGridUtil.setSavedColumnWidth(localStorageItemPrefix, params);
                }}
                keepNonExistentRowsSelected
                onColumnOrderChange={onColumnOrderChange}
                editMode="cell"
                onCellDoubleClick={onCellDoubleClick}
                onCellEditStop={onCellEditStop}
                disableAggregation
                disablePivoting
                showToolbar
              />
            </Paper>
          </Box>
        </Box>
      </Box>
    </>
  );
});

export default V2SalesOrdersPresenter;
